/**
 * Mixpanel Event Constants
 *
 * This file defines all analytics events tracked in the web app.
 * Event names are synchronized with the mobile app (globe-thrivers-app) to ensure
 * consistent cross-platform analytics.
 *
 * IMPORTANT: These event names match the mobile app exactly. Do not rename events
 * without coordinating with the mobile team to maintain analytics consistency.
 */

// ============================================================================
// USER LIFECYCLE EVENTS
// ============================================================================

/** User successfully completed signup */
export const EVENT_SIGNUP = 'Signup';

/** User successfully logged in */
export const EVENT_LOGIN = 'Login';

/** Auth modal opened (web auth drawer) */
export const EVENT_AUTH_MODAL_OPENED = 'Auth Modal Opened';

/** Auth modal dismissed without completing auth */
export const EVENT_AUTH_MODAL_DISMISSED = 'Auth Modal Dismissed';

/** User selected an auth method (google, apple, email) */
export const EVENT_AUTH_METHOD_SELECTED = 'Auth Method Selected';

/** Auth method selection step viewed */
export const EVENT_AUTH_METHOD_STEP_VIEWED = 'Auth Method Step Viewed';

/** User toggled between login and signup modes */
export const EVENT_AUTH_MODE_TOGGLED = 'Auth Mode Toggled';

/** User clicked forgot password link */
export const EVENT_FORGOT_PASSWORD_CLICKED = 'Forgot Password Clicked';

/** Password reset verification code sent */
export const EVENT_RESET_CODE_SENT = 'Reset Code Sent';

/** Password reset completed successfully */
export const EVENT_PASSWORD_RESET_COMPLETED = 'Password Reset Completed';

/** Social sign-in matched an existing account and the link sheet was shown */
export const EVENT_ACCOUNT_LINK_PROMPT_SHOWN = 'Account Link Prompt Shown';

/** Account link finished after the required sign-in again */
export const EVENT_ACCOUNT_LINK_COMPLETED = 'Account Link Completed';

/** User dismissed the account link collision sheet without linking */
export const EVENT_ACCOUNT_LINK_PROMPT_DISMISSED = 'Account Link Prompt Dismissed';

/** User started verifying an existing account to complete linking (collision sheet) */
export const EVENT_ACCOUNT_LINK_VERIFY_STARTED = 'Account Link Verify Started';

/** Account link or settings link attempt failed (non-cancel) */
export const EVENT_ACCOUNT_LINK_FAILED = 'Account Link Failed';

/** User expanded “Why am I seeing this?” on the collision sheet */
export const EVENT_ACCOUNT_LINK_HELP_EXPANDED = 'Account Link Help Expanded';

/** User opened Account Settings (menu or OAuth return) */
export const EVENT_ACCOUNT_SETTINGS_OPENED = 'Account Settings Opened';

/** Sign-in methods loaded in Account Settings */
export const EVENT_SIGN_IN_METHODS_VIEWED = 'Sign-in Methods Viewed';

/** User started linking Google or Apple from Account Settings */
export const EVENT_SIGN_IN_METHOD_LINK_STARTED = 'Sign-in Method Link Started';

/** User finished linking Google or Apple from Account Settings */
export const EVENT_SIGN_IN_METHOD_LINK_COMPLETED = 'Sign-in Method Link Completed';

/** User removed Google or Apple from Account Settings */
export const EVENT_SIGN_IN_METHOD_REMOVED = 'Sign-in Method Removed';

/** User added an email password from Account Settings */
export const EVENT_SIGN_IN_PASSWORD_ADDED = 'Sign-in Password Added';

/** Unlink blocked (no password, last method) by UI rule or backend 409 */
export const EVENT_SIGN_IN_METHOD_BLOCKED = 'Sign-in Method Blocked';

/** User requested a change-password code from Account Settings */
export const EVENT_CHANGE_PASSWORD_STARTED = 'Change Password Started';

/** User changed their password from Account Settings */
export const EVENT_CHANGE_PASSWORD_COMPLETED = 'Change Password Completed';

/** User opened the delete-account confirmation */
export const EVENT_DELETE_ACCOUNT_STARTED = 'Delete Account Started';

/** Auth transition splash shown between email auth and onboarding */
export const EVENT_AUTH_TRANSITION_SHOWN = 'Auth Transition Shown';

/** User selected their role during onboarding */
export const EVENT_ONBOARDING_ROLE_SELECTED = 'Onboarding: Role Selected';

/** User completed the user details step during onboarding */
export const EVENT_ONBOARDING_USER_DETAILS_COMPLETED = 'Onboarding: User Details Completed';

/** Creator completed the creator questionnaire during onboarding */
export const EVENT_ONBOARDING_CREATOR_QUEST_COMPLETED = 'Onboarding: Creator Quest Completed';

/** User completed the full onboarding flow */
export const EVENT_ONBOARDING_COMPLETED = 'Onboarding: Completed';

/** Onboarding step viewed */
export const EVENT_ONBOARDING_STEP_VIEWED = 'Onboarding: Step Viewed';

/** User clicked Continue and advanced from an onboarding step */
export const EVENT_ONBOARDING_STEP_CONTINUED = 'Onboarding: Step Continued';

/** User clicked Back during onboarding */
export const EVENT_ONBOARDING_STEP_BACK_CLICKED = 'Onboarding: Step Back Clicked';

/** User jumped to another onboarding step via progress controls / shortcuts */
export const EVENT_ONBOARDING_STEP_JUMPED = 'Onboarding: Step Jumped';

/** Onboarding step skipped */
export const EVENT_ONBOARDING_STEP_SKIPPED = 'Onboarding: Step Skipped';

/** User selected an import method during onboarding (social or copy-paste) */
export const EVENT_ONBOARDING_IMPORT_METHOD_SELECTED = 'Onboarding: Import Method Selected';

/** Import social URL validation failed during onboarding */
export const EVENT_ONBOARDING_IMPORT_SOCIAL_VALIDATION_FAILED = 'Onboarding: Import Social Validation Failed';

/** User taps sample reel autofill in import social step */
export const EVENT_ONBOARDING_IMPORT_SOCIAL_SAMPLE_REEL_CLICKED =
  'Onboarding: Import Social Sample Reel Clicked';

/** Import notes validation failed during onboarding */
export const EVENT_ONBOARDING_IMPORT_NOTES_VALIDATION_FAILED = 'Onboarding: Import Notes Validation Failed';

/** Traveler complete screen CTA/card clicked */
export const EVENT_ONBOARDING_TRAVELER_COMPLETE_ACTION = 'Onboarding: Traveler Complete Action';

/** Creator complete screen storefront CTA clicked */
export const EVENT_ONBOARDING_CREATOR_STOREFRONT_CTA = 'Onboarding: Creator Storefront CTA';

/** Advisor complete screen card clicked */
export const EVENT_ONBOARDING_ADVISOR_CARD_CLICKED = 'Onboarding: Advisor Card Clicked';

/** Advisor complete screen storefront CTA clicked */
export const EVENT_ONBOARDING_ADVISOR_STOREFRONT_CTA = 'Onboarding: Advisor Storefront CTA';

/** User opened a personalization field during onboarding */
export const EVENT_ONBOARDING_PERSONALIZATION_FIELD_OPENED = 'Onboarding: Personalization Field Opened';

/** User updated a personalization field during onboarding */
export const EVENT_ONBOARDING_PERSONALIZATION_FIELD_UPDATED = 'Onboarding: Personalization Field Updated';

/** Onboarding auto-created list and trip successfully */
export const EVENT_ONBOARDING_LISTS_CREATED = 'Onboarding: Lists Created';

/** Onboarding auto-creation of list/trip failed */
export const EVENT_ONBOARDING_LISTS_CREATION_FAILED = 'Onboarding: Lists Creation Failed';

/**
 * Final createUser rejected with ONBOARDING_INCOMPLETE.
 * Surfaces backend missing_fields so drop-off at complete CTAs can be
 * distinguished from soft abandon.
 */
export const EVENT_ONBOARDING_COMPLETION_FAILED = 'Onboarding: Completion Failed';

/** New user created */
export const EVENT_NEW_USER = 'New User';

/** User updated their profile information */
export const EVENT_USER_PROFILE_UPDATE = 'User Profile Update';

/** User opened the Edit Profile screen */
export const EVENT_EDIT_PROFILE_OPENED = 'Edit Profile Opened';

/** User updated their profile photo */
export const EVENT_PROFILE_PHOTO_UPDATED = 'Profile Photo Updated';

/** User updated their hero/cover image */
export const EVENT_HERO_PHOTO_UPDATED = 'Hero Photo Updated';

/** User deleted their account */
export const EVENT_ACCOUNT_DELETED = 'Account Deleted';

/** User opened the Metrics bottom-sheet drawer on their profile */
export const EVENT_METRICS_DRAWER_OPENED = 'Metrics Drawer Opened';

/** User tapped a row inside the Metrics drawer (fires before opening the per-stat modal) */
export const EVENT_METRIC_TAPPED = 'Metric Tapped';

// ============================================================================
// APP LIFECYCLE EVENTS (native iOS / Android)
// ============================================================================

/** App cold-started or brought to foreground (native only) */
export const EVENT_APP_OPENED = 'App Opened';

/** App moved to background (native only) */
export const EVENT_APP_BACKGROUNDED = 'App Backgrounded';

/** App returned to foreground after backgrounding (native only) */
export const EVENT_APP_RESUMED = 'App Resumed';

/** A deep link URL was opened and handled by the app */
export const EVENT_DEEP_LINK_OPENED = 'Deep Link Opened';

/** First launch ever — fired once per install */
export const EVENT_APP_INSTALLED = 'App Installed';

// ============================================================================
// NAVIGATION EVENTS
// ============================================================================

/** User viewed a page/screen (matches mobile's screen view tracking) */
export const EVENT_VISIT_CONTENT = 'Visit_Content';

// ============================================================================
// CONTENT INTERACTION EVENTS
// ============================================================================

/** User liked a post */
export const EVENT_LIKE_CLICKED = 'Like clicked';

/** User unliked a post */
export const EVENT_UNLIKE_CLICKED = 'Unlike clicked';

/** User saved/bookmarked content */
export const EVENT_SAVE_CLICKED = 'Save clicked';

/** User clicked save button on place card in list view (opens SaveToOverlay) */
export const EVENT_PLACE_SAVE_TO_ITINERARY_CLICKED = 'Place Save to Itinerary Clicked';

/** User saved a place to an existing itinerary */
export const EVENT_PLACE_SAVED_TO_ITINERARY = 'Place Saved to Itinerary';

/** User saved a place to a newly created itinerary */
export const EVENT_PLACE_SAVED_TO_NEW_ITINERARY = 'Place Saved to New Itinerary';

/** User bookmarked content (alternative to save) */
export const EVENT_BOOKMARK_CLICKED = 'Bookmark clicked';

/** User clicked share button */
export const EVENT_SHARE_CLICKED = 'Share clicked';

/** User shared content publicly (created share link) */
export const EVENT_SHARED_PUBLICLY = 'Shared Publicly';

/** Content type for share tracking (post type when sharing a post) */
export type ShareContentType = 'list' | 'itinerary' | 'place';

/** Map post_type to ShareContentType for share analytics */
export function getShareContentType(postType: string | undefined): ShareContentType | undefined {
  if (!postType) return undefined;
  const lower = postType.toLowerCase();
  if (lower === 'list') return 'list';
  if (lower === 'itinerary') return 'itinerary';
  if (lower === 'place') return 'place';
  return undefined;
}

/** User clicked reply button */
export const EVENT_REPLY_CLICKED = 'Reply clicked';

/** User successfully posted a reply */
export const EVENT_SUCCESSFUL_POST_REPLY = 'Successful post reply';

// ============================================================================
// LOCATION/PLACE INTERACTION EVENTS
// ============================================================================

/** User recommended a location */
export const EVENT_LOCATION_RECOMMEND_CLICKED = 'Location recommend clicked';

/** User unrecommended a location */
export const EVENT_LOCATION_UNRECOMMEND_CLICKED = 'Location unrecommend clicked';

/** User clicked on a location tag */
export const EVENT_LOCATION_TAG_CLICKED = 'Location Tag clicked';

/** User bookmarked a location (POST /api/v2/location-save) */
export const EVENT_LOCATION_SAVED = 'Location Saved';

/** User removed a location bookmark (POST /api/v2/location-unsave) */
export const EVENT_LOCATION_UNSAVED = 'Location Unsaved';

/** Location save failed (id resolution or API error) */
export const EVENT_LOCATION_SAVE_FAILED = 'Location Save Failed';

/** User tapped "Add to a trip or list" — split-button chevron or legacy prompt CTA */
export const EVENT_LOCATION_SAVE_ADD_TO_TRIP_CLICKED = 'Location Save Add to Trip Clicked';

/** Mini-CTA shown after a successful location save (icon-mode bookmark). */
export const EVENT_LOCATION_SAVE_PROMPT_SHOWN = 'Location Save Prompt: Shown';

/** Mini-CTA dismissed without continuing to SaveToOverlay. */
export const EVENT_LOCATION_SAVE_PROMPT_DISMISSED = 'Location Save Prompt: Dismissed';

export type SavePromptDismissMethod = 'backdrop' | 'escape' | 'close_button';

/**
 * Place Modal opened from the Profile Saves tab (saved-location row tap).
 * Added to the `PlaceModalEntryPoint` union for analytics funnel accuracy.
 */
export const EVENT_PLACE_MODAL_ENTRY_PROFILE_SAVES = 'profile_saves_location' as const;

/**
 * Place Modal opened from My Travel → Saved (saved-location card tap).
 */
export const EVENT_PLACE_MODAL_ENTRY_MY_TRAVEL_SAVES = 'my_travel_saves_location' as const;

/**
 * Place Modal opened from the Profile Places tab (recommendation card tap).
 */
export const EVENT_PLACE_MODAL_ENTRY_PROFILE_PLACES = 'profile_places_recommendation' as const;

/**
 * Place Modal opened from the Profile Countries tab (map pin or place card tap).
 */
export const EVENT_PLACE_MODAL_ENTRY_PROFILE_COUNTRIES = 'profile_countries_location' as const;

/**
 * Place Modal opened from the Profile Stays tab (hotel-stop card or map pin tap).
 */
export const EVENT_PLACE_MODAL_ENTRY_PROFILE_STAYS = 'profile_stays_location' as const;

/** Place detail modal opened (pin click or hotel card click) */
export const EVENT_PLACE_MODAL_OPENED = 'Place Modal Opened';

/**
 * Where the Place Modal was opened from.
 * - `list_pin` — sequence pin or timeline row click on a list page (/list/[id])
 * - `list_map` — map marker click on a list page
 * - `place_hero` — hero image click on the /place page
 * - `place_map` — map marker click on the /place page
 * - `post_map` — map marker click on a legacy post view
 * - `profile_saves_location` — saved-location row tap on the Profile Saves tab
 * - `my_travel_saves_location` — saved-location card tap on My Travel Saved
 * - `profile_places_recommendation` — recommendation card tap on the Profile Places tab
 * - `profile_countries_location` — map pin or place card tap on the Profile Countries tab
 * - `profile_stays_location` — hotel-stop card or map pin tap on the Profile Stays tab
 * - `performance_dashboard` — recommendation card tap on the Performance Dashboard feed
 */
export type PlaceModalEntryPoint =
  | 'list_pin'
  | 'list_map'
  | 'place_hero'
  | 'place_map'
  | 'post_map'
  | 'profile_saves_location'
  | 'my_travel_saves_location'
  | 'profile_places_recommendation'
  | 'profile_countries_location'
  | 'profile_stays_location'
  | 'performance_dashboard'
  | string;

/** User switched tabs inside the place detail modal. */
export const EVENT_PLACE_MODAL_TAB_SELECTED = 'Place Modal Tab Selected';

/** User clicked Create a Trip from a place detail modal */
export const EVENT_PLACE_CREATE_TRIP_CLICKED = 'Place Create Trip Clicked';

/** New itinerary successfully created from place detail modal */
export const EVENT_PLACE_CREATE_TRIP_SUCCEEDED = 'Place Create Trip Succeeded';

// ============================================================================
// CONTENT CREATION EVENTS - Posts
// ============================================================================

/** User updated tags on a post */
export const EVENT_CREATE_POST_TAGS_UPDATED = 'Create Post: Tags Updated';

/** User clicked next button in post creation */
export const EVENT_CREATE_POST_NEXT_CLICKED = 'Create Post: Next Button Clicked';

/** User updated cover photos on a post */
export const EVENT_CREATE_POST_COVER_PHOTOS_UPDATED = 'Create Post: Cover Photos Updated';

/** User saved and published a post */
export const EVENT_CREATE_POST_SAVED_PUBLISHED = 'Create Post: Post Saved and Published';

/** User saved a post as draft */
export const EVENT_CREATE_POST_SAVED_DRAFT = 'Create Post: Post Saved as Draft';

/** User created content (remix, new post, etc.) - matches mobile app event */
export const EVENT_CREATE_CONTENT = 'Create_Content';

// ============================================================================
// CONTENT CREATION EVENTS - Lists
// ============================================================================

/** User clicked button to create new list */
export const EVENT_CREATE_LIST_BUTTON_CLICKED = 'Create List: Create New List Button Clicked';

/** User added a location to a list */
export const EVENT_CREATE_LIST_LOCATION_ADDED = 'Create List: Location Added';

/** User removed a location from a list */
export const EVENT_CREATE_LIST_LOCATION_REMOVED = 'Create List: Location Removed';

/** User reordered places in a list */
export const EVENT_CREATE_LIST_PLACES_REORDERED = 'Create List: Places Reordered';

/** Owner published a list (transitioned from private draft to public) */
export const EVENT_CREATE_LIST_PUBLISHED = 'Create List: Published';

// ============================================================================
// CONTENT CREATION EVENTS - Itineraries
// ============================================================================

/** User navigated to itinerary question screen */
export const EVENT_CREATE_ITINERARY_NEXT_TO_QUESTION = 'Create Itinerary: Next to Question Screen';

/** User selected destination for itinerary */
export const EVENT_CREATE_ITINERARY_DESTINATION_SELECTED = 'Create Itinerary: Destination Selected';

/** User selected dates for itinerary */
export const EVENT_CREATE_ITINERARY_DATES_SELECTED = 'Create Itinerary: Dates Selected';

/** User selected trip type for itinerary */
export const EVENT_CREATE_ITINERARY_TRIP_TYPE_SELECTED = 'Create Itinerary: Trip Type Selected';

/** User added places via AI to itinerary */
export const EVENT_CREATE_ITINERARY_PLACES_ADDED_AI = 'Create Itinerary: Places Added via AI';

/** User reordered places in an itinerary */
export const EVENT_CREATE_ITINERARY_PLACES_REORDERED = 'Create Itinerary: Places Reordered';

/** User added a location/stop to an itinerary (manual, search, or user-created) */
export const EVENT_CREATE_ITINERARY_LOCATION_ADDED = 'Create Itinerary: Location Added';

/** User removed a location/stop from an itinerary */
export const EVENT_CREATE_ITINERARY_LOCATION_REMOVED = 'Create Itinerary: Location Removed';

/** User moved a location/stop to another day from the place card menu */
export const EVENT_CREATE_ITINERARY_LOCATION_MOVED = 'Create Itinerary: Location Moved';

/** User opened the day/date editor (pencil icon on hero band) */
export const EVENT_ITINERARY_DATES_EDIT_OPENED = 'Itinerary Dates: Edit Opened';

/** User converted between dated and undated itinerary. Properties: { from, to, dayCount } */
export const EVENT_ITINERARY_DATES_CONVERTED = 'Itinerary Dates: Converted';

/** User clicked Start in CreateItineraryModal (modal-based creation flow) */
export const EVENT_CREATE_TRIP_STARTED = 'Create Trip: Started';

/** New itinerary post successfully created via CreateItineraryModal */
export const EVENT_CREATE_TRIP_COMPLETED = 'Create Trip: Completed';

/** Owner published an itinerary from the editor (Post button, transitions private to public) */
export const EVENT_CREATE_ITINERARY_PUBLISHED = 'Create Itinerary: Published';

/** Advisor help sheet shown to a viewer of an advisor's itinerary or list. Properties: { surface: 'itinerary' | 'list', post_id, advisor_id, is_guest } */
export const EVENT_ADVISOR_HELP_SHEET_VIEWED = 'Advisor Help Sheet: Viewed';

/** Viewer tapped the advisor help pop-up CTA (opens the trip request flow). Properties: { surface, post_id, advisor_id, is_guest, sheet_state: 'expanded' } */
export const EVENT_ADVISOR_HELP_SHEET_ACCEPTED = 'Advisor Help Sheet: Accepted';

/** Viewer tapped "Just browsing" on the advisor help sheet (collapses it to the sticky panel). Properties: { surface, post_id, advisor_id, is_guest } */
export const EVENT_ADVISOR_HELP_SHEET_DISMISSED = 'Advisor Help Sheet: Dismissed';

/** Viewer tapped the "Ask [Name] about…" sticky bar (opens the trip request flow). Properties: { surface, post_id, advisor_id, is_guest } */
export const EVENT_ADVISOR_STICKY_BAR_TAPPED = 'Advisor Sticky Bar: Tapped';

/** Trip request sent to an advisor from their itinerary or list. Properties: { surface, post_id, advisor_id, is_guest, entry: 'popup' | 'sticky_bar' | 'resumed', request_id, delivery_status } */
export const EVENT_ADVISOR_TRIP_REQUEST_SUBMITTED = 'Advisor Trip Request: Submitted';

/** "Make this trip yours" remix pop-up shown on another user's itinerary. Properties: { surface: 'itinerary', post_id, owner_id, is_guest } */
export const EVENT_REMIX_PROMPT_VIEWED = 'Remix Prompt: Viewed';

/** Viewer tapped "Remix this trip" on the remix pop-up. Properties: { surface, post_id, owner_id, is_guest } */
export const EVENT_REMIX_PROMPT_ACCEPTED = 'Remix Prompt: Accepted';

/** Viewer tapped "Just browsing" on the remix pop-up. Properties: { surface, post_id, owner_id, is_guest } */
export const EVENT_REMIX_PROMPT_DISMISSED = 'Remix Prompt: Dismissed';

/** "Make this trip yours" / "Remix this trip →" sticky bar shown on another user's itinerary (once per page view). Properties: { surface: 'itinerary', post_id, owner_id, is_guest } */
export const EVENT_REMIX_STICKY_BAR_VIEWED = 'Remix Sticky Bar: Viewed';

/** Viewer tapped the remix sticky bar CTA (opens the remix flow; guests get the sign-up modal directly). Properties: { surface, post_id, owner_id, is_guest } */
export const EVENT_REMIX_STICKY_BAR_TAPPED = 'Remix Sticky Bar: Tapped';

// ============================================================================
// PLACE PHOTOS (slot-level, list & itinerary edit)
// ============================================================================

/** User saved place photos from the place photo gallery (one event per successful save, not per file) */
export const EVENT_PLACE_PHOTOS_SAVED = 'Place Photos Saved';

/** Viewer opened the all-photos grid from the /place hero band Photos button */
export const EVENT_PLACE_PHOTOS_GRID_OPENED = 'Recommend Place: Photos Grid Opened';

/** Viewer clicked the hero bar Save button on a /place page (opens SaveToOverlay) */
export const EVENT_RECOMMEND_PLACE_HERO_SAVE_CLICKED = 'Recommend Place: Hero Save Clicked';

// ============================================================================
// CONTENT CREATION EVENTS - Place Recommendations
// ============================================================================

/** User clicked button to recommend a place */
export const EVENT_RECOMMEND_PLACE_BUTTON_CLICKED = 'Recommend Place: Button Clicked';

/** User viewed the Recommend a Place create/edit screen */
export const EVENT_RECOMMEND_PLACE_SCREEN_VIEWED = 'Recommend Place: Screen Viewed';

/** User selected a place in the recommend a place flow */
export const EVENT_RECOMMEND_PLACE_LOCATION_SELECTED = 'Recommend Place: Location Selected';

/** User published a place recommendation */
export const EVENT_RECOMMEND_PLACE_PUBLISHED = 'Recommend Place: Published';

/** User saved a place recommendation as draft */
export const EVENT_RECOMMEND_PLACE_SAVED_DRAFT = 'Recommend Place: Saved as Draft';

/** Viewer toggled the Save pill in the /place hero band */
export const EVENT_RECOMMEND_PLACE_HERO_SAVE_TOGGLED = 'Recommend Place: Hero Save Toggled';

// ============================================================================
// CONTENT CREATION EVENTS - Enriched Location Recommendations
// ============================================================================

/** User opened the Recommend This Place modal (enriched location recommendation) */
export const EVENT_ENRICHED_REC_MODAL_OPENED = 'Enriched Rec: Modal Opened';

/** User submitted an enriched recommendation (upsert succeeded) */
export const EVENT_ENRICHED_REC_SUBMITTED = 'Enriched Rec: Submitted';

/** User edited an existing enriched recommendation */
export const EVENT_ENRICHED_REC_EDITED = 'Enriched Rec: Edited';

/** User deleted an enriched recommendation */
export const EVENT_ENRICHED_REC_DELETED = 'Enriched Rec: Deleted';

/** User created a share link for an enriched recommendation */
export const EVENT_ENRICHED_REC_SHARE_LINK_CREATED = 'Enriched Rec: Share Link Created';

/** Shared recommendation page viewed (public `?token=` or authenticated owner without token) */
export const EVENT_ENRICHED_REC_PUBLIC_PAGE_VIEWED = 'Enriched Rec: Public Page Viewed';

/** Web-only: user dismissed the read-only shared recommendation modal (view mode) */
export const EVENT_ENRICHED_REC_VIEW_MODAL_DISMISSED = 'Enriched Rec: View Modal Dismissed';

/** User clicked the "recommendations" link in the post-publish banner to view their recs */
export const EVENT_RECS_BANNER_LINK_CLICKED = 'Recs: Banner Link Clicked';

/** User opened the profile “Your Recommendations” / user recs modal (`UserRecommendationsModal`) */
export const EVENT_RECS_MODAL_OPENED = 'Recs: Modal Opened';

/** User dismissed the recommendations modal (backdrop, close control, or child `onCloseParent`) */
export const EVENT_RECS_MODAL_CLOSED = 'Recs: Modal Closed';

/** User switched between Saved and Planned tabs (own profile only) */
export const EVENT_RECS_MODAL_TAB_SELECTED = 'Recs: Modal Tab Selected';

/** In-modal engagement: edit, share, post counts, or opening a place on the map */
export const EVENT_RECS_MODAL_ACTION = 'Recs: Modal Action';

export type RecsModalAction =
  | 'edit_open'
  | 'share_open'
  | 'itineraries_open'
  | 'lists_open'
  | 'places_open'
  | 'location_row_map_open';

// ============================================================================
// SEARCH & DISCOVERY EVENTS
// ============================================================================

/** User submitted a search query */
export const EVENT_SEARCH_TEXT_SUBMITTED = 'Search Text Submitted';

/** Autocomplete request initiated for location search */
export const EVENT_LOCATION_AUTOCOMPLETE_REQUEST = 'Location Autocomplete Request';

/** Autocomplete successfully returned results */
export const EVENT_LOCATION_AUTOCOMPLETE_SUCCESS = 'Location Autocomplete Success';

/** Autocomplete returned empty response */
export const EVENT_LOCATION_AUTOCOMPLETE_EMPTY = 'Location Autocomplete Empty Response';

/** Autocomplete request failed */
export const EVENT_LOCATION_AUTOCOMPLETE_FAILED = 'Location Autocomplete Failed';

// ============================================================================
// PROFILE & SOCIAL EVENTS
// ============================================================================

/** User viewed a profile screen */
export const EVENT_PROFILE_SCREEN_VIEWED = 'Profile Screen viewed';

/** User viewed their own profile */
export const EVENT_MY_PROFILE_SCREEN_VIEWED = 'My Profile Screen viewed';

/** User followed a creator */
export const EVENT_FOLLOW_CREATOR_CLICKED = 'Follow Creator clicked';

/** User unfollowed a creator */
export const EVENT_UNFOLLOW_CREATOR_CLICKED = 'Unfollow Creator clicked';

/** User tapped a creator card during onboarding follow-creators step */
export const EVENT_ONBOARDING_CREATOR_CARD_CLICKED = 'Onboarding: Creator Card Clicked';

/** User tapped "Show More" on the follow-creators onboarding step (legacy; superseded by Next/Previous) */
export const EVENT_ONBOARDING_FOLLOW_CREATORS_SHOW_MORE = 'Onboarding: Follow Creators Show More';

/** User tapped Next on the follow-creators onboarding pager */
export const EVENT_ONBOARDING_FOLLOW_CREATORS_NEXT = 'Onboarding: Follow Creators Next';

/** User tapped Previous on the follow-creators onboarding pager */
export const EVENT_ONBOARDING_FOLLOW_CREATORS_PREVIOUS = 'Onboarding: Follow Creators Previous';

/** User viewed notifications screen */
export const EVENT_NOTIFICATIONS_SCREEN_VIEWED = 'Notifications Screen viewed';

// ============================================================================
// PUSH NOTIFICATION EVENTS (iOS native)
// ============================================================================

export type PushOptInEntryPoint =
  | 'onboarding'
  | 'main_shell'
  | 'login'
  | 'notifications_banner';

/** Soft explainer sheet shown before OS push permission dialog */
export const EVENT_PUSH_PERMISSION_PROMPT_SHOWN = 'Push Permission Prompt Shown';

/** User granted push permission via OS dialog */
export const EVENT_PUSH_PERMISSION_GRANTED = 'Push Permission Granted';

/** User denied push permission via OS dialog */
export const EVENT_PUSH_PERMISSION_DENIED = 'Push Permission Denied';

/** APNS device token successfully POSTed to backend */
export const EVENT_PUSH_TOKEN_REGISTERED = 'Push Token Registered';

/** Push received while app is foregrounded/backgrounded */
export const EVENT_PUSH_RECEIVED = 'Push Received';

/** User tapped a push notification */
export const EVENT_PUSH_OPENED = 'Push Opened';

/** User opened notifications panel/screen */
export const EVENT_NOTIFICATIONS_PANEL_OPENED = 'Notifications Panel Opened';

/** Opened notifications were auto-marked read after dwell delay */
export const EVENT_NOTIFICATIONS_AUTO_MARK_ALL_READ = 'Notifications Auto Mark All Read';

/** User viewed city selection screen */
export const EVENT_CITY_SCREEN_VIEWED = 'City Screen viewed';

// ============================================================================
// AI FEATURES EVENTS
// ============================================================================

/** User started AI trip generation */
export const EVENT_AI_TRIP_GENERATION_STARTED = 'AI Trip Generation Started';

/** AI successfully generated a trip */
export const EVENT_AI_TRIP_GENERATED = 'AI Trip Generated';

// ============================================================================
// BOOKING EVENTS
// ============================================================================

/** User initiated booking flow - indicates high intent to book */
export const EVENT_HIGH_INTENT_TRIP = 'High_Intent_Trip';

/** User submitted a booking request */
export const EVENT_SUBMIT_BOOKING_REQUEST = 'Submit_Booking_Request';

/** User successfully booked a hotel */
export const EVENT_USER_BOOKED_HOTEL = 'User Booked Hotel';

/** User clicked a GetYourGuide experiences outbound deep link */
export const EVENT_EXPERIENCE_OUTBOUND_CLICK = 'Experience Outbound Click';

/** Browser blocked the GYG outbound tab from opening */
export const EVENT_EXPERIENCE_OUTBOUND_POPUP_BLOCKED = 'Experience Outbound Popup Blocked';

/** GYG experiences widget container mounted */
export const EVENT_EXPERIENCE_WIDGET_SHOWN = 'Experience Widget Shown';

/** Hotel booking CTA rendered (place modal Book your stay / timeline Check Availability) */
export const EVENT_HOTEL_WIDGET_SHOWN = 'Hotel Widget Shown';

/** User clicked a Stay22 Allez outbound deeplink */
export const EVENT_STAY22_OUTBOUND_CLICK = 'Stay22 Outbound Click';

/** Browser blocked the Stay22 outbound tab from opening */
export const EVENT_STAY22_OUTBOUND_POPUP_BLOCKED = 'Stay22 Outbound Popup Blocked';

/** Stay22 Map opened in a new tab (embed URL) */
export const EVENT_STAY22_MAP_OPENED = 'Stay22 Map Opened';

/** @deprecated Use EVENT_STAY22_MAP_OPENED — map no longer opens as an in-app sheet */
export const EVENT_STAY22_MAP_SHEET_OPENED = EVENT_STAY22_MAP_OPENED;

/** User opened place stay booking choice (Nuitee vs Stay22) */
export const EVENT_PLACE_STAY_BOOK_CHOICE_OPENED = 'Place Stay Book Choice Opened';

/** User selected Nuitee or Stay22 in place stay booking choice */
export const EVENT_PLACE_STAY_BOOK_CHOICE_SELECTED = 'Place Stay Book Choice Selected';

/** User accepted an AI suggestion */
export const EVENT_AI_SUGGESTION_ACCEPTED = 'AI Suggestion Accepted';

// ============================================================================
// BOOKING MODAL EVENTS
// ============================================================================

/** Booking modal opened (Book button clicked) */
export const EVENT_BOOKING_MODAL_OPENED = 'Booking Modal Opened';

/** Booking modal closed by the user */
export const EVENT_BOOKING_MODAL_CLOSED = 'Booking Modal Closed';

/** Hotel detail view loaded with data */
export const EVENT_BOOKING_HOTEL_VIEWED = 'Booking Hotel Viewed';

/** User clicked a tab in the hotel detail view */
export const EVENT_BOOKING_HOTEL_TAB_CLICKED = 'Booking Hotel Tab Clicked';

/** User changed dates or guests and re-ran search */
export const EVENT_BOOKING_SEARCH_UPDATED = 'Booking Search Updated';

/** User clicked "Search nearby hotels" */
export const EVENT_BOOKING_NEARBY_HOTELS_CLICKED = 'Booking Nearby Hotels Clicked';

/** User toggled a rate filter (breakfast included / refundable only) */
export const EVENT_BOOKING_RATE_FILTER_APPLIED = 'Booking Rate Filter Applied';

/** User opened the full image gallery */
export const EVENT_BOOKING_GALLERY_OPENED = 'Booking Gallery Opened';

/** User opened the facilities modal */
export const EVENT_BOOKING_FACILITIES_MODAL_OPENED = 'Booking Facilities Modal Opened';

/** User opened the reviews modal */
export const EVENT_BOOKING_REVIEWS_MODAL_OPENED = 'Booking Reviews Modal Opened';

/** User selected a room/rate and moved to checkout */
export const EVENT_BOOKING_ROOM_SELECTED = 'Booking Room Selected';

/** Prebook API call succeeded */
export const EVENT_BOOKING_PREBOOK_SUCCEEDED = 'Booking Prebook Succeeded';

/** Prebook API call failed */
export const EVENT_BOOKING_PREBOOK_FAILED = 'Booking Prebook Failed';

/** Payment SDK rendered and is ready for input */
export const EVENT_BOOKING_PAYMENT_FORM_LOADED = 'Booking Payment Form Loaded';

/** Nuitee SSO new tab opened successfully (tab-first pattern) */
export const EVENT_BOOKING_SSO_TAB_OPENED = 'Booking SSO Tab Opened';

/** Nuitee SSO new tab was blocked by the browser popup blocker */
export const EVENT_BOOKING_SSO_POPUP_BLOCKED = 'Booking SSO Popup Blocked';

/** Nuitee SSO API call failed (hotel-id lookup or getSSOLink) */
export const EVENT_BOOKING_SSO_API_ERROR = 'Booking SSO API Error';

// ============================================================================
// WEB-ONLY EVENTS (Not on mobile)
// ============================================================================

/** Book online vs advisor choice modal opened (header or explore advisors section) */
export const EVENT_BOOK_CHOICE_MODAL_OPENED = 'Book Choice Modal Opened';

/** User chose an option in the book choice modal */
export const EVENT_BOOK_CHOICE_OPTION_SELECTED = 'Book Choice Option Selected';

/** User submitted social media link for InspoToTrip */
export const EVENT_INSPO_LINK_SUBMITTED = 'Inspo Link Submitted';

/** Trip successfully created from social media inspiration */
export const EVENT_INSPO_TRIP_CREATED = 'Inspo Trip Created';

/** Native deep link opened the dedicated social import route. */
export const EVENT_INSPO_IMPORT_DEEP_LINK_OPENED = 'Inspo Import Deep Link Opened';

/** Social scrape request started on dedicated import route. */
export const EVENT_INSPO_IMPORT_SCRAPE_STARTED = 'Inspo Import Scrape Started';

/** Social scrape request succeeded on dedicated import route. */
export const EVENT_INSPO_IMPORT_SCRAPE_SUCCEEDED = 'Inspo Import Scrape Succeeded';

/** Social scrape request failed on dedicated import route. */
export const EVENT_INSPO_IMPORT_SCRAPE_FAILED = 'Inspo Import Scrape Failed';

/** URL validation failed before scrape started on dedicated import route. */
export const EVENT_INSPO_IMPORT_URL_VALIDATION_FAILED = 'Inspo Import URL Validation Failed';

/** User opened destination picker after selecting imported spots. */
export const EVENT_INSPO_IMPORT_DESTINATION_PICKER_OPENED = 'Inspo Import Destination Picker Opened';

/** User tapped the suggested New List card to fast-path save to a new list. */
export const EVENT_INSPO_IMPORT_SUGGESTED_LIST_TAPPED = 'Inspo Import Suggested List Tapped';

/** User selected create type (trip/list) after scrape. */
export const EVENT_INSPO_IMPORT_CREATE_STARTED = 'Inspo Import Create Started';

/** Trip or list creation succeeded from dedicated import route. */
export const EVENT_INSPO_IMPORT_CREATE_SUCCEEDED = 'Inspo Import Create Succeeded';

/** Trip or list creation failed from dedicated import route. */
export const EVENT_INSPO_IMPORT_CREATE_FAILED = 'Inspo Import Create Failed';

/** Inspo Import flow modal/page was opened (any entry point). */
export const EVENT_INSPO_IMPORT_FLOW_OPENED = 'Inspo Import Flow Opened';

/** User cancelled or closed the Inspo Import flow without completing. */
export const EVENT_INSPO_IMPORT_FLOW_CANCELLED = 'Inspo Import Flow Cancelled';

/** Copy & Paste Import flow modal/page was opened (any entry point). */
export const EVENT_COPYPASTE_IMPORT_FLOW_OPENED = 'Copy Paste Import Flow Opened';

/** Text parse request started on Copy & Paste import route. */
export const EVENT_COPYPASTE_IMPORT_SCRAPE_STARTED = 'Copy Paste Import Scrape Started';

/** Text parse request succeeded on Copy & Paste import route. */
export const EVENT_COPYPASTE_IMPORT_SCRAPE_SUCCEEDED = 'Copy Paste Import Scrape Succeeded';

/** Text parse request failed on Copy & Paste import route. */
export const EVENT_COPYPASTE_IMPORT_SCRAPE_FAILED = 'Copy Paste Import Scrape Failed';

/** User toggled a place selection in Copy & Paste import. */
export const EVENT_COPYPASTE_IMPORT_PLACE_TOGGLED = 'Copy Paste Import Place Toggled';

/** User started saving Copy & Paste import to a list or trip. */
export const EVENT_COPYPASTE_IMPORT_CREATE_STARTED = 'Copy Paste Import Create Started';

/** Copy & Paste import save succeeded. */
export const EVENT_COPYPASTE_IMPORT_CREATE_SUCCEEDED = 'Copy Paste Import Create Succeeded';

/** User tapped Done after Copy & Paste import save. */
export const EVENT_COPYPASTE_IMPORT_DONE_CLICKED = 'Copy Paste Import Done Clicked';

/** User cancelled or closed the Copy & Paste Import flow without completing. */
export const EVENT_COPYPASTE_IMPORT_FLOW_CANCELLED = 'Copy Paste Import Flow Cancelled';

/** User submitted text for Copy & Paste import parsing. */
export const EVENT_COPYPASTE_IMPORT_TEXT_SUBMITTED = 'Copy Paste Import Text Submitted';

/** Copy & Paste import text validation failed before parsing. */
export const EVENT_COPYPASTE_IMPORT_VALIDATION_FAILED = 'Copy Paste Import Validation Failed';

/** Copy & Paste import destination picker opened. */
export const EVENT_COPYPASTE_IMPORT_DESTINATION_PICKER_OPENED = 'Copy Paste Import Destination Picker Opened';

/** User tapped a suggested list shortcut during Copy & Paste import. */
export const EVENT_COPYPASTE_IMPORT_SUGGESTED_LIST_TAPPED = 'Copy Paste Import Suggested List Tapped';

/** Copy & Paste import save failed. */
export const EVENT_COPYPASTE_IMPORT_CREATE_FAILED = 'Copy Paste Import Create Failed';

/** Bulk save primary CTA tapped — one `saveLocation` per selected place. */
export const EVENT_COPYPASTE_IMPORT_BULK_SAVE_STARTED = 'Copy Paste Import Bulk Save Started';

/** Bulk save completed with at least one saved place. */
export const EVENT_COPYPASTE_IMPORT_BULK_SAVED = 'Copy Paste Import Bulk Saved';

/** Bulk save completed with zero saves (all attempts failed). */
export const EVENT_COPYPASTE_IMPORT_BULK_SAVE_FAILED = 'Copy Paste Import Bulk Save Failed';

/** Trip Import flow opened (PDF, Word, or pasted text). */
export const EVENT_TRIP_IMPORT_FLOW_OPENED = 'Trip Import Flow Opened';

/** Trip Import flow cancelled before save. */
export const EVENT_TRIP_IMPORT_FLOW_CANCELLED = 'Trip Import Flow Cancelled';

/** User started uploading a PDF or Word file for trip import. */
export const EVENT_TRIP_IMPORT_UPLOAD_STARTED = 'Trip Import Upload Started';

/** Trip import file upload failed. */
export const EVENT_TRIP_IMPORT_UPLOAD_FAILED = 'Trip Import Upload Failed';

/** Trip import parse request started. */
export const EVENT_TRIP_IMPORT_PARSE_STARTED = 'Trip Import Parse Started';

/** Trip import parse succeeded. */
export const EVENT_TRIP_IMPORT_PARSE_SUCCEEDED = 'Trip Import Parse Succeeded';

/** Trip import parse failed. */
export const EVENT_TRIP_IMPORT_PARSE_FAILED = 'Trip Import Parse Failed';

/** User switched trip vs list on trip import review. */
export const EVENT_TRIP_IMPORT_RESULT_TYPE_SWITCHED = 'Trip Import Result Type Switched';

/** User toggled a place on trip import review. */
export const EVENT_TRIP_IMPORT_PLACE_TOGGLED = 'Trip Import Place Toggled';

/** User picked a save destination in list mode. */
export const EVENT_TRIP_IMPORT_DESTINATION_SELECTED = 'Trip Import Destination Selected';

/** User started saving trip import (trip or list). */
export const EVENT_TRIP_IMPORT_CREATE_STARTED = 'Trip Import Create Started';

/** Trip import save succeeded. */
export const EVENT_TRIP_IMPORT_CREATE_SUCCEEDED = 'Trip Import Create Succeeded';

/** Trip import save failed. */
export const EVENT_TRIP_IMPORT_CREATE_FAILED = 'Trip Import Create Failed';

/** User tapped View trip after trip import save. */
export const EVENT_TRIP_IMPORT_VIEW_TRIP_CLICKED = 'Trip Import View Trip Clicked';

/** User tapped View list after trip import list save. */
export const EVENT_TRIP_IMPORT_VIEW_LIST_CLICKED = 'Trip Import View List Clicked';

export const EVENT_TRIP_IMPORT_REVIEW_FILTER_CHANGED = 'Trip Import Review Filter Changed';

/** Social import review — user opened trip/list picker to append places. */
export const EVENT_TRIP_IMPORT_ADD_TO_EXISTING_CLICKED = 'Trip Import Add To Existing Clicked';

export const EVENT_TRIP_IMPORT_DETAILED_TOGGLED = 'Trip Import Detailed Toggled';

/** User expanded or collapsed place description on import review. */
export const EVENT_TRIP_IMPORT_PLACE_DETAILS_TOGGLED = 'Trip Import Place Details Toggled';

export const EVENT_TRIP_IMPORT_KEEP_EDITING_CLICKED = 'Trip Import Keep Editing Clicked';

export const EVENT_TRIP_IMPORT_PUBLISH_CLICKED = 'Trip Import Publish Clicked';

export const EVENT_TRIP_IMPORT_PUBLISHED = 'Trip Import Published';

export const EVENT_TRIP_IMPORT_PUBLISH_FAILED = 'Trip Import Publish Failed';

/** User matched an unmatched import row to a Google place before save. */
export const EVENT_TRIP_IMPORT_PLACE_MATCHED = 'Trip Import Place Matched';

/** User upgraded a custom stop to a Google place, or matched one during import review. */
export const EVENT_CUSTOM_PLACE_UPGRADED = 'Custom Place Upgraded';

export type CustomPlaceUpgradeEntry = 'timeline' | 'add_place' | 'import_review';

/** User toggled a place selection on/off in the parsed results. */
export const EVENT_INSPO_IMPORT_PLACE_TOGGLED = 'Inspo Import Place Toggled';

/** User clicked Done on the save-success screen and navigated to the saved destination. */
export const EVENT_INSPO_IMPORT_DONE_CLICKED = 'Inspo Import Done Clicked';

/** Bulk save primary CTA tapped — one `saveLocation` per selected place. */
export const EVENT_INSPO_IMPORT_BULK_SAVE_STARTED = 'Inspo Import Bulk Save Started';

/** Bulk save completed with at least one saved place. */
export const EVENT_INSPO_IMPORT_BULK_SAVED = 'Inspo Import Bulk Saved';

/** Bulk save completed with zero saves (all attempts failed). */
export const EVENT_INSPO_IMPORT_BULK_SAVE_FAILED = 'Inspo Import Bulk Save Failed';

/** User viewed admin dashboard */
export const EVENT_ADMIN_DASHBOARD_SCREEN_VIEWED = 'Admin Dashboard Screen viewed';

/** Admin hid a post */
export const EVENT_ADMIN_POST_HIDDEN = 'Admin Post Hidden';

/** Admin viewed a user's profile */
export const EVENT_ADMIN_USER_VIEWED = 'Admin User Viewed';

/** Admin opened Platform Performance tab */
export const EVENT_ADMIN_PLATFORM_PERFORMANCE_VIEWED = 'Admin Platform Performance Viewed';

/** Admin changed Platform Performance date range */
export const EVENT_ADMIN_PLATFORM_DATE_RANGE_CHANGED = 'Admin Platform Performance Date Range Changed';

/** Admin opened creator drill-down on Platform Performance */
export const EVENT_ADMIN_PLATFORM_CREATOR_DRILLDOWN_OPENED = 'Admin Platform Performance Creator Drilldown Opened';

/** Admin opened GYG Attribution tab */
export const EVENT_ADMIN_GYG_ATTRIBUTION_VIEWED = 'Admin GYG Attribution Viewed';

/** Admin submitted GYG CSV import */
export const EVENT_ADMIN_GYG_IMPORT_SUBMITTED = 'Admin GYG Import Submitted';

/** Admin GYG CSV import succeeded */
export const EVENT_ADMIN_GYG_IMPORT_SUCCEEDED = 'Admin GYG Import Succeeded';

/** Admin GYG CSV import failed */
export const EVENT_ADMIN_GYG_IMPORT_FAILED = 'Admin GYG Import Failed';

/** User created a share link */
export const EVENT_SHARE_LINK_CREATED = 'Share Link Created';

/** Someone viewed content via share link */
export const EVENT_SHARE_LINK_VIEWED = 'Share Link Viewed';

/** Backend content impression was recorded (fire-and-forget POST to view endpoint) */
export const EVENT_CONTENT_VIEW_TRACKED = 'Content View Tracked';

/** Backend profile page impression was recorded (fire-and-forget POST to view endpoint) */
export const EVENT_PROFILE_VIEW_TRACKED = 'Profile View Tracked';

/** How the user opened the unified Share modal (publish, share icon, or deep link) */
export type ShareModalEntry =
  | 'post_publish'
  | 'share_icon'
  | 'deep_link'
  | 'admin'
  | 'other';

/** Share modal opened (includes post-publish celebration path and icon/deep-link entry) */
export const EVENT_SHARE_MODAL_OPENED = 'Share Modal Opened';

/** User closed Share modal without copying the link (close, backdrop, or escape) */
export const EVENT_SHARE_MODAL_DISMISSED = 'Share Modal Dismissed';

/** How the user dismissed the Share modal */
export type ShareModalDismissMethod = 'close' | 'backdrop' | 'escape';

// ============================================================================
// LOCATION SHARE EVENTS
// ============================================================================

/** User tapped the share button on a location (place modal) */
export const EVENT_LOCATION_SHARE_CLICKED = 'Location Share Clicked';

/** Location share link was successfully generated */
export const EVENT_LOCATION_SHARE_LINK_CREATED = 'Location Share Link Created';

/** Recipient landed on the profile-first location share page (/profile/:id?token=&shared_location_id=) */
export const EVENT_LOCATION_SHARE_PAGE_VIEWED = 'Location Share Page Viewed';

/** Place modal was opened from a location share deep link */
export const EVENT_LOCATION_SHARE_MODAL_OPENED = 'Location Share Modal Opened';

// ============================================================================
// LOGGED OUT WELCOME SCREEN EVENTS (mobile / native)
// ============================================================================

/** Logged out user viewed the Welcome splash (mobile / native) */
export const EVENT_LOGGED_OUT_WELCOME_SCREEN_VIEWED = 'Logged Out Welcome Screen Viewed';

/** @deprecated Carousel removed — retained for analytics backwards compatibility */
export const EVENT_LOGGED_OUT_WELCOME_SLIDE_VIEWED = 'Logged Out Welcome Slide Viewed';

/** Logged out user tapped "Get Started" on the Welcome splash */
export const EVENT_LOGGED_OUT_WELCOME_GET_STARTED_CLICKED = 'Logged Out Welcome Get Started Clicked';

/** Logged out user tapped "Continue to Explore" on the Welcome splash */
export const EVENT_LOGGED_OUT_WELCOME_CONTINUE_EXPLORE_CLICKED = 'Logged Out Welcome Continue Explore Clicked';

/** @deprecated Use EVENT_LOGGED_OUT_WELCOME_GET_STARTED_CLICKED */
export const EVENT_LOGGED_OUT_WELCOME_CREATE_ACCOUNT_CLICKED = 'Logged Out Welcome Create Account Clicked';

/** @deprecated Login moved into auth drawer from Welcome splash */
export const EVENT_LOGGED_OUT_WELCOME_LOGIN_CLICKED = 'Logged Out Welcome Login Clicked';

// ============================================================================
// LOGGED OUT USER TRACKING EVENTS
// ============================================================================

/** Logged out user viewed home page */
export const EVENT_LOGGED_OUT_HOME_PAGE_VIEWED = 'Logged Out Home Page Viewed';

/** Logged out user's home page content finished loading */
export const EVENT_LOGGED_OUT_HOME_PAGE_CONTENT_LOADED = 'Logged Out Home Page Content Loaded';

/** Logged out user clicked on a post card */
export const EVENT_LOGGED_OUT_POST_CARD_CLICKED = 'Logged Out Post Card Clicked';

/** Logged out user clicked save button on post card (triggers auth CTA) */
export const EVENT_LOGGED_OUT_POST_SAVE_CLICKED = 'Logged Out Post Save Clicked';

/** Logged out user clicked on a creator card */
export const EVENT_LOGGED_OUT_CREATOR_CARD_CLICKED = 'Logged Out Creator Card Clicked';

/** Logged out user clicked follow button on creator card (triggers auth CTA) */
export const EVENT_LOGGED_OUT_CREATOR_FOLLOW_CLICKED = 'Logged Out Creator Follow Clicked';

/** Logged out user initiated city search (triggers auth CTA) */
export const EVENT_LOGGED_OUT_CITY_SEARCH_INITIATED = 'Logged Out City Search Initiated';

/** Logged out user clicked trip type filter */
export const EVENT_LOGGED_OUT_TRIP_TYPE_FILTER_CLICKED = 'Logged Out Trip Type Filter Clicked';

/** Logged out user toggled map visibility */
export const EVENT_LOGGED_OUT_MAP_TOGGLE_CLICKED = 'Logged Out Map Toggle Clicked';

/** Logged out user reached scroll depth milestone */
export const EVENT_LOGGED_OUT_HOME_PAGE_SCROLL_DEPTH = 'Logged Out Home Page Scroll Depth';

/** Logged out user time on page milestone */
export const EVENT_LOGGED_OUT_HOME_PAGE_TIME_ON_PAGE = 'Logged Out Home Page Time on Page';

/** Logged out user viewed post page */
export const EVENT_LOGGED_OUT_POST_VIEW_PAGE_VIEWED = 'Logged Out Post View Page Viewed';

/** Logged out user's post view content finished loading */
export const EVENT_LOGGED_OUT_POST_VIEW_CONTENT_LOADED = 'Logged Out Post View Content Loaded';

/** Logged out user clicked on a place card */
export const EVENT_LOGGED_OUT_PLACE_CARD_CLICKED = 'Logged Out Place Card Clicked';

/** Logged out user clicked save button on place card (triggers auth CTA) */
export const EVENT_LOGGED_OUT_PLACE_SAVE_CLICKED = 'Logged Out Place Save Clicked';

/** Logged out user clicked on numbered indicator (Lists only) */
export const EVENT_LOGGED_OUT_PLACE_NUMBER_INDICATOR_CLICKED = 'Logged Out Place Number Indicator Clicked';

/** Logged out user reached scroll depth milestone on post view */
export const EVENT_LOGGED_OUT_POST_VIEW_SCROLL_DEPTH = 'Logged Out Post View Scroll Depth';

/** Logged out user time on page milestone on post view */
export const EVENT_LOGGED_OUT_POST_VIEW_TIME_ON_PAGE = 'Logged Out Post View Time on Page';

/** Logged out user clicked on map marker */
export const EVENT_LOGGED_OUT_POST_VIEW_MAP_MARKER_CLICKED = 'Logged Out Post View Map Marker Clicked';

/** Logged out user toggled map visibility on post view */
export const EVENT_LOGGED_OUT_POST_VIEW_MAP_TOGGLE_CLICKED = 'Logged Out Post View Map Toggle Clicked';

/** Logged out user toggled itinerary view mode (Itineraries only) */
export const EVENT_LOGGED_OUT_ITINERARY_VIEW_MODE_TOGGLED = 'Logged Out Itinerary View Mode Toggled';

/** Auth CTA popover was shown to logged out user */
export const EVENT_LOGGED_OUT_AUTH_CTA_SHOWN = 'Logged Out Auth CTA Shown';

/** Logged out user clicked sign up button in auth CTA */
export const EVENT_LOGGED_OUT_AUTH_CTA_SIGN_UP_CLICKED = 'Logged Out Auth CTA Sign Up Clicked';

/** Logged out user dismissed auth CTA popover */
export const EVENT_LOGGED_OUT_AUTH_CTA_DISMISSED = 'Logged Out Auth CTA Dismissed';

/** Logged out user viewed profile page */
export const EVENT_LOGGED_OUT_PROFILE_VIEW_PAGE_VIEWED = 'Logged Out Profile View Page Viewed';

/** Logged out user viewed Creators page */
export const EVENT_LOGGED_OUT_CREATORS_PAGE_VIEWED = 'Logged Out Creators Page Viewed';

/** Logged out user viewed Advisors page */
export const EVENT_LOGGED_OUT_ADVISORS_PAGE_VIEWED = 'Logged Out Advisors Page Viewed';

/** Logged out user viewed Destinations page */
export const EVENT_LOGGED_OUT_DESTINATIONS_PAGE_VIEWED = 'Logged Out Destinations Page Viewed';

/** Logged out user viewed Trips page */
export const EVENT_LOGGED_OUT_TRIPS_PAGE_VIEWED = 'Logged Out Trips Page Viewed';

/** Logged out user clicked See All button */
export const EVENT_LOGGED_OUT_SEE_ALL_CLICKED = 'Logged Out See All Clicked';

/** Logged out user clicked on a destination card */
export const EVENT_LOGGED_OUT_DESTINATION_CARD_CLICKED = 'Logged Out Destination Card Clicked';

// ============================================================================
// LOGGED IN HOME / EXPLORE EVENTS
// ============================================================================

/** Logged in user's home page content finished loading */
export const EVENT_LOGGED_IN_HOME_PAGE_CONTENT_LOADED = 'Logged In Home Page Content Loaded';

/** Logged in user reached scroll depth milestone on home */
export const EVENT_LOGGED_IN_HOME_PAGE_SCROLL_DEPTH = 'Logged In Home Page Scroll Depth';

/** Logged in user time on page milestone on home */
export const EVENT_LOGGED_IN_HOME_PAGE_TIME_ON_PAGE = 'Logged In Home Page Time on Page';

/** Itineraries browse page featured content finished loading */
export const EVENT_ITINERARIES_BROWSE_CONTENT_LOADED = 'Itineraries Browse Content Loaded';

/** Logged in user clicked See All button (any section) */
export const EVENT_SEE_ALL_CLICKED = 'See All Clicked';

/** User clicked a search suggestion prompt (home hero) */
export const EVENT_SEARCH_PROMPT_CLICKED = 'Search Prompt Clicked';

/** Hero section collapsed (scroll) */
export const EVENT_HERO_COLLAPSED = 'Hero Collapsed';

/** Hero section expanded (scroll) */
export const EVENT_HERO_EXPANDED = 'Hero Expanded';

/** Logged in user clicked a card on home (creator, advisor, trip, destination, or travel style) */
export const EVENT_HOME_CARD_CLICKED = 'Home Card Clicked';

/** Logged in user viewed Creators page */
export const EVENT_CREATORS_SCREEN_VIEWED = 'Creators Screen viewed';

/** Logged in user viewed Advisors page */
export const EVENT_ADVISORS_SCREEN_VIEWED = 'Advisors Screen viewed';

/** Logged in user viewed Destinations page */
export const EVENT_DESTINATIONS_SCREEN_VIEWED = 'Destinations Screen viewed';

/** Logged in user viewed Trips page */
export const EVENT_TRIPS_SCREEN_VIEWED = 'Trips Screen viewed';

// ============================================================================
// BROWSE PAGES - CONTENT LOADED & CARD CLICKS
// ============================================================================

/** Creators page content finished loading */
export const EVENT_CREATORS_PAGE_CONTENT_LOADED = 'Creators Page Content Loaded';

/** Destinations page content finished loading */
export const EVENT_DESTINATIONS_PAGE_CONTENT_LOADED = 'Destinations Page Content Loaded';

/** Travel Styles page content finished loading */
export const EVENT_TRAVEL_STYLES_PAGE_CONTENT_LOADED = 'Travel Styles Page Content Loaded';

/** Advisors page content finished loading */
export const EVENT_ADVISORS_PAGE_CONTENT_LOADED = 'Advisors Page Content Loaded';

/** Trips page content finished loading */
export const EVENT_TRIPS_PAGE_CONTENT_LOADED = 'Trips Page Content Loaded';

/** List route page content finished loading (cache hit or network) */
export const EVENT_LIST_PAGE_CONTENT_LOADED = 'List Page Content Loaded';

/** Place route page content finished loading (cache hit or network) */
export const EVENT_PLACE_PAGE_CONTENT_LOADED = 'Place Page Content Loaded';

/** Itinerary route page content finished loading (cache hit or network) */
export const EVENT_ITINERARY_PAGE_CONTENT_LOADED = 'Itinerary Page Content Loaded';

/** Profile page core content finished loading (cache hit or network) */
export const EVENT_PROFILE_PAGE_CONTENT_LOADED = 'Profile Page Content Loaded';

/** Share link flavor for OPEN guest vs authenticated tokenized shares */
export type ShareLinkType = 'canonical' | 'tokenized';

/** Logged out user viewed Travel Styles page */
export const EVENT_LOGGED_OUT_TRAVEL_STYLES_PAGE_VIEWED = 'Logged Out Travel Styles Page Viewed';

/** Logged in user viewed Travel Styles page */
export const EVENT_TRAVEL_STYLES_SCREEN_VIEWED = 'Travel Styles Screen viewed';

/** User clicked a creator card on Creators page (logged in) */
export const EVENT_CREATORS_PAGE_CARD_CLICKED = 'Creators Page Card Clicked';

/** User clicked an advisor card on Advisors page (logged in) */
export const EVENT_ADVISORS_PAGE_CARD_CLICKED = 'Advisors Page Card Clicked';

/** User clicked a trip card on Trips page (logged in) */
export const EVENT_TRIPS_PAGE_CARD_CLICKED = 'Trips Page Card Clicked';

/** User clicked a destination card on Destinations page (logged in) */
export const EVENT_DESTINATIONS_PAGE_CARD_CLICKED = 'Destinations Page Card Clicked';

/** User clicked travel style on Travel Styles page (logged in) */
export const EVENT_TRAVEL_STYLE_CLICKED = 'Travel Style Clicked';

/** User clicked prev/next in See All carousel */
export const EVENT_SEE_ALL_NAVIGATION_CLICKED = 'See All Navigation Clicked';

/** User applied a filter on a browse page (e.g. traveller type) */
export const EVENT_PAGE_FILTER_APPLIED = 'Page Filter Applied';

/** User selected a city/destination from search on Destinations or Results */
export const EVENT_RESULTS_DESTINATION_SELECTED = 'Results Destination Selected';

/** User clicked Remix on a post/trip */
export const EVENT_REMIX_CLICKED = 'Remix Clicked';

/** Remixed copy of an itinerary created from the itinerary page. Properties: { post_id, new_post_id, owner_id, owner_profile_type, source: 'remix_prompt' | 'hero' } */
export const EVENT_REMIX_CREATED = 'Remix Created';

// ============================================================================
// RESULTS PAGE EVENTS
// ============================================================================

/** User viewed Results page */
export const EVENT_RESULTS_PAGE_VIEWED = 'Results Page Viewed';

/** Results page content finished loading */
export const EVENT_RESULTS_PAGE_CONTENT_LOADED = 'Results Page Content Loaded';

/** User changed tab on Results page */
export const EVENT_RESULTS_TAB_CHANGED = 'Results Tab Changed';

/** User panned or zoomed the Results map, so the results show only that map area */
export const EVENT_RESULTS_MAP_AREA_SEARCHED = 'Results Map Area Searched';

/**
 * User tapped a tab in the mobile bottom tab bar.
 * Payload `tab`: `'home' | 'search' | 'performance' | 'content' | 'profile'` (plus opens the create menu and is not tracked here).
 */
export const EVENT_MOBILE_TAB_TAPPED = 'Mobile Tab Bar: Tab Tapped';

/** User tapped a menu item in the mobile tab bar plus menu */
export const EVENT_MOBILE_PLUS_MENU_ITEM_CLICKED = 'Mobile Tab Bar: Plus Menu Item Clicked';

/** Performance Dashboard viewed */
export const EVENT_PERFORMANCE_DASHBOARD_VIEWED = 'Performance: Dashboard Viewed';

/** Performance date filter changed */
export const EVENT_PERFORMANCE_DATE_FILTER_CHANGED = 'Performance: Date Filter Changed';

/** Link Performance hero card tapped */
export const EVENT_PERFORMANCE_LINK_TAPPED = 'Performance: Link Performance Tapped';

/** Recommendation card tapped on performance dashboard */
export const EVENT_PERFORMANCE_REC_CARD_TAPPED = 'Performance: Rec Card Tapped';

/** User expanded content view breakdown on performance dashboard */
export const EVENT_PERFORMANCE_VIEWS_EXPANDED = 'Performance: Views Expanded';

/** User tapped a stat card on performance dashboard */
export const EVENT_PERFORMANCE_STAT_CARD_TAPPED = 'Performance: Stat Card Tapped';

/** User tapped a CTA on the performance dashboard */
export const EVENT_PERFORMANCE_CTA_TAPPED = 'Performance: CTA Tapped';

/** User changed a content filter on performance pages */
export const EVENT_PERFORMANCE_CONTENT_FILTER_CHANGED = 'Performance: Content Filter Changed';

/** User interacted with a coming-soon stub on performance pages */
export const EVENT_PERFORMANCE_COMING_SOON_TAPPED = 'Performance: Coming Soon Tapped';

/** User opened content from My Content or Link Performance list */
export const EVENT_PERFORMANCE_CONTENT_OPENED = 'Performance: Content Opened';

/** Link Performance page viewed */
export const EVENT_LINK_PERFORMANCE_PAGE_VIEWED = 'Link Performance: Page Viewed';

/** Link Performance back button tapped */
export const EVENT_LINK_PERFORMANCE_BACK_TAPPED = 'Link Performance: Back Tapped';

/** Link Performance search used */
export const EVENT_LINK_PERFORMANCE_SEARCH_USED = 'Link Performance: Search Used';

/** Link Performance sort changed */
export const EVENT_LINK_PERFORMANCE_SORT_CHANGED = 'Link Performance: Sort Changed';

/** My Travel page viewed */
export const EVENT_MY_TRAVEL_PAGE_VIEWED = 'My Travel: Page Viewed';

/** Saved page viewed */
export const EVENT_SAVED_PAGE_VIEWED = 'Saved: Page Viewed';

/** My Travel tab changed */
export const EVENT_MY_TRAVEL_TAB_CHANGED = 'My Travel: Tab Changed';

/** My Travel saved filter changed */
export const EVENT_MY_TRAVEL_SAVED_FILTER_CHANGED = 'My Travel: Saved Filter Changed';

/** My Travel country bucket opened */
export const EVENT_MY_TRAVEL_COUNTRY_OPENED = 'My Travel: Country Opened';

/** My Travel US state bucket opened */
export const EVENT_MY_TRAVEL_STATE_OPENED = 'My Travel: State Opened';

/** My Travel build trip clicked */
export const EVENT_MY_TRAVEL_BUILD_TRIP_CLICKED = 'My Travel: Build Trip Clicked';

/** Link Performance row expanded for place breakdown */
export const EVENT_LINK_PERFORMANCE_ROW_EXPANDED = 'Link Performance: Row Expanded';

/** Link Performance share URL copied */
export const EVENT_LINK_PERFORMANCE_LINK_COPIED = 'Link Performance: Link Copied';

/** Create New Link picker opened */
export const EVENT_LINK_PERFORMANCE_CREATE_OPENED = 'Link Performance: Create Link Opened';

/** Share link created from Link Performance picker */
export const EVENT_LINK_PERFORMANCE_LINK_CREATED = 'Link Performance: Link Created';

/** User refined search on Results page (in-page keyword) */
export const EVENT_RESULTS_SEARCH_REFINED = 'Results Search Refined';

/** User clicked a trip card on Results page */
export const EVENT_RESULTS_TRIP_CARD_CLICKED = 'Results Trip Card Clicked';

/** User clicked a creator card on Results page */
export const EVENT_RESULTS_CREATOR_CARD_CLICKED = 'Results Creator Card Clicked';

/** User clicked an advisor card on Results page */
export const EVENT_RESULTS_ADVISOR_CARD_CLICKED = 'Results Advisor Card Clicked';

/** User changed a filter on Results page (country, travel_style, travel_with) */
export const EVENT_RESULTS_FILTER_CHANGED = 'Results Filter Changed';

/** User toggled map visibility on Results page */
export const EVENT_RESULTS_MAP_TOGGLE_CLICKED = 'Results Map Toggle Clicked';

// ============================================================================
// ERROR TRACKING EVENTS
// ============================================================================

/** API request failed */
export const EVENT_API_REQUEST_FAILED = 'API Request Failed';

/** Form validation error occurred */
export const EVENT_FORM_VALIDATION_ERROR = 'Form Validation Error';

/** Authentication error occurred */
export const EVENT_AUTH_ERROR = 'Auth Error';

// ============================================================================
// EVENT PROPERTY TYPES
// ============================================================================

/**
 * Common properties included in all events
 */
export interface BaseEventProperties {
  /** Platform identifier */
  platform: 'web' | 'mobile_app';
  /** ISO timestamp */
  timestamp: string;
  /** UTM source parameter */
  utm_source?: string;
  /** UTM medium parameter */
  utm_medium?: string;
  /** UTM campaign parameter */
  utm_campaign?: string;
  /** UTM content parameter */
  utm_content?: string;
  /** UTM term parameter */
  utm_term?: string;
}

/**
 * Properties for Visit_Content (page view) events
 */
export interface VisitContentProperties extends BaseEventProperties {
  /** Name of the page being viewed */
  page_name: string;
  /** URL path */
  path?: string;
  /** Referrer URL */
  referrer?: string;
  /** Full URL */
  url?: string;
  /** Original acquisition source */
  original_source?: string;
  /** Original acquisition medium */
  original_medium?: string;
  /** Original acquisition campaign */
  original_campaign?: string;
}

/**
 * Properties for signup events
 */
export interface SignupProperties extends BaseEventProperties {
  /** User's unique ID */
  user_id: string;
  /** User's email */
  email: string;
  /** User's name */
  name: string;
  /** User's profile type */
  profile_type: string;
}

/**
 * Properties for content interaction events (like, save, share, etc.)
 */
export interface ContentInteractionProperties extends BaseEventProperties {
  /** Post/content ID - Note: matches mobile's "postID" with capital ID */
  postID?: string;
  /** Location ID - Note: matches mobile's "locationId" with lowercase Id */
  locationID?: string;
  /** Additional context */
  [key: string]: unknown;
}

/**
 * Properties for search events
 */
export interface SearchProperties extends BaseEventProperties {
  /** Search query text */
  searchText?: string;
  /** Number of results returned */
  resultCount?: number;
  /** Search context/filters */
  [key: string]: unknown;
}

/**
 * Properties for content creation events
 */
export interface ContentCreationProperties extends BaseEventProperties {
  /** Post/content ID */
  postID?: string;
  /** Itinerary ID */
  itineraryId?: string;
  /** List ID */
  listId?: string;
  /** Content type */
  contentType?: string;
  /** Additional metadata */
  [key: string]: unknown;
}

/**
 * Properties for error events
 */
export interface ErrorProperties extends BaseEventProperties {
  /** Error message */
  errorMessage?: string;
  /** Error type/category */
  errorType?: string;
  /** Error code */
  errorCode?: string;
  /** Additional error context */
  [key: string]: unknown;
}

/**
 * Base properties for logged-out user events
 */
export interface LoggedOutEventProperties extends BaseEventProperties {
  /** Always 'logged_out' for these events */
  user_state: 'logged_out';
  /** Name of the page where event occurred */
  page_name: string;
  /** Device type */
  device_type: 'desktop' | 'mobile';
}
